import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { RightBridgeApiService } from './right-bridge-api.service';
import { SessionStorageService } from './session-storage.service';
import { Observable, throwError as observableThrowError, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { WarnDialogComponent } from '../warn-dialog/warn-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { NavHistoryService } from './nav-history.service';

interface rightNeeded {
  rights: rightNeeded[] | string[];
  operation: string;
}

@Injectable()
export class AuthService {
  private rbs = inject(RightBridgeApiService);
  private ss = inject(SessionStorageService);
  router = inject(Router);
  private titleService = inject(Title);
  private dialog = inject(MatDialog);
  private navHistory = inject(NavHistoryService);

  private evaluateRights(
    rightNeeded: string | rightNeeded,
    rights: string[]
  ): boolean {
    if (typeof rightNeeded === 'string') {
      // If single right or the end of the recursion
      return rights.includes(rightNeeded);
    }

    if (rightNeeded.rights && Array.isArray(rightNeeded.rights)) {
      // Nested rights structure
      const operation = rightNeeded.operation || 'and'; // Default to 'and' if not specified

      if (operation === 'and') {
        return rightNeeded.rights.every((nestedRight: rightNeeded | string) =>
          this.evaluateRights(nestedRight, rights)
        );
      } else if (operation === 'or') {
        return rightNeeded.rights.some((nestedRight: rightNeeded | string) =>
          this.evaluateRights(nestedRight, rights)
        );
      }
    }

    return false; // Default to deny access if structure is invalid
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    const session = this.ss.get('session') || null;
    const rights = this.ss.get('rights') || null;
    const rightNeeded = route.data.rightNeeded;
    const appNeeded = route.data.appNeeded;

    if (session) {
      if (
        rights &&
        rights.includes('CarrierPortal') &&
        state.url !== '/carrier-portal' &&
        state.url !== '/carrier-policy-details'
      ) {
        this.router.navigate(['/carrier-portal']);
      }
      if (
        rights &&
        rights.includes('PostTradeReadOnly') &&
        state.url.includes('aml-monitor')
      ) {
        this.router.navigate(['/post-trade']);
      } else {
        return this.rbs.querySession(session).pipe(
          map(res => {
            const auth = +res.results > 0;

            if (auth) {
              if (
                (!rightNeeded || this.evaluateRights(rightNeeded, rights)) &&
                (!appNeeded || rights.includes(appNeeded))
              ) {
                this.checkApp(state.url);
                return true;
              } else {
                let navDialog = this.dialog.open(WarnDialogComponent, {
                  minWidth: '35vw',
                  maxWidth: '75vw',
                  panelClass: 'warn-dialog',
                  data: {
                    headline: 'Warning',
                    content:
                      'This account does not have the correct permissions to view this page. Please reach out to your administrator if you believe this is an error.',
                    confirm: 'OK',
                    hideCancel: true,
                  },
                });
                navDialog.afterClosed().subscribe(() => {
                  navDialog = null;
                });
                if (
                  !this.navHistory.getHistory() ||
                  this.navHistory.getHistory().length <= 1
                ) {
                  return this.router.createUrlTree(['']);
                } else {
                  return false;
                }
              }
            } else {
              this.rbs.logout();
              return this.router.createUrlTree(['/login']);
            }
          }),
          catchError((error: any) => {
            observableThrowError(() => error.error || 'Server error');
            this.rbs.logout();
            return of(this.router.createUrlTree(['/login']));
          })
        );
      }
    } else {
      return of(this.router.createUrlTree(['/login']));
    }
  }

  checkApp(url) {
    const app = this.ss.get('currentApp');
    let newApp: string;

    const startPage = this.ss.get('startPage');

    if (
      url &&
      url.split('/')[1].toLowerCase() !== 'dashboard' &&
      url.split('/')[1].toLowerCase() !== 'reporting' &&
      url.split('/')[1].toLowerCase() !== 'downloads' &&
      url.split('/')[1].toLowerCase() !== 'admin' &&
      url.split('/')[1].toLowerCase() !== 'account' &&
      url.split('/')[1].toLowerCase() !== 'case' &&
      url.split('/')[1].toLowerCase() !== 'case-explorer' &&
      url.split('/')[1].toLowerCase() !== 'rep-behavior' &&
      url.split('/')[1].toLowerCase() !== 'search'
    ) {
      newApp = url.split('/')[1];
    } else if (url.split('/')[1].toLowerCase() === 'case') {
      newApp = url.split('/')[2];
    } else if (startPage) {
      newApp = startPage;
    } else {
      newApp = app;
    }
    if (url && newApp !== app) {
      this.ss.set('currentApp', newApp);
      this.rbs.setParams(newApp);
    }
    switch (newApp) {
      case 'aw':
        this.titleService.setTitle('Annuity Wizard');
        break;
      case 'iw':
        this.titleService.setTitle('Investment Wizard');
        break;
      case 'pp':
        this.titleService.setTitle('Product Profiler');
        break;
      case 'cp':
        this.titleService.setTitle('Client Profiler');
        break;
      case 'li':
        this.titleService.setTitle('Life Insurance Wizard');
        break;
      case 'se':
        this.titleService.setTitle('Suggestion Engine');
        break;
      case 'rw':
        this.titleService.setTitle('Rollover Wizard');
        break;
      default:
        this.titleService.setTitle('RightBridge');
    }
  }
}
